// src/components/ProductDetailsModal/index.js
import React, { useState, useEffect } from 'react';
import { getRestaurantConfigSync } from '../../config/restaurantConfig';
import { X, Plus, Minus, ShoppingCart, AlertCircle } from 'lucide-react';
import './index.css';
import { settingsService } from '../../config/settingsService';
import FallbackImg from '../FallbackImg';
import SpiceLevel from '../SpiceLevel';

const ProductDetailsModal = ({
  isOpen,
  onClose,
  product,
  onAddToCart,
  onOpenCustomization,
  isUserLoggedIn,
  cart = [],
  generateCartItemKey,
  onUpdateQuantity,
  onRemove
}) => {
  const [quantity, setQuantity] = useState(1);

  // ✅ Find product in cart
  const getCartItemKey = () => {
    if (!product) return null;
    if (generateCartItemKey && typeof generateCartItemKey === 'function') {
      return generateCartItemKey(product);
    }
    // Fallback: simple key for uncustomized items
    return `simple-${product.id}`;
  };

  const cartItemKey = getCartItemKey();
  const cartItem = cartItemKey ? cart.find(item => item.cartItemKey === cartItemKey) : null;
  const cartQuantity = cartItem?.quantity || 0;

  useEffect(() => {
    if (isOpen) {
      setQuantity(1);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !product) return null;

  // Check if product has customization options
  const hasVariations = product.variations &&
    typeof product.variations === 'object' &&
    Object.keys(product.variations).length > 0;

  const hasExtras = product.extras &&
    Array.isArray(product.extras) &&
    product.extras.length > 0;

  const hasAddons = product.addons &&
    Array.isArray(product.addons) &&
    product.addons.length > 0;

  const needsCustomization = hasVariations || hasExtras || hasAddons;

  // Stock status
  const isTracked = product.is_tracked || false;
  const availableQty = product.available_stock;
  const isOutOfStock = isTracked && !product.is_available;
  const isLimitedStock = isTracked && product.is_available && availableQty !== null && availableQty <= 5;

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    if (newQty >= 1) {
      if (isTracked && availableQty !== null) {
        if (newQty <= availableQty) {
          setQuantity(newQty);
        } else {
          alert(`Only ${availableQty} units available`);
        }
      } else {
        setQuantity(newQty);
      }
    }
  };

  const handleIncreaseQuantity = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onUpdateQuantity) {
      onUpdateQuantity(cartItemKey, 1);
    }
  };

  const handleDecreaseQuantity = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onUpdateQuantity) {
      onUpdateQuantity(cartItemKey, -1);
    }
  };


  const handleAddToCart = () => {
    if (isOutOfStock) {
      alert(`Sorry, ${product.name} is currently out of stock.`);
      return;
    }

    if (needsCustomization) {
      onClose();
      onOpenCustomization(product);
    } else {
      const productToAdd = {
        ...product,
        quantity: quantity,
        variations: [],
        extras: [],
        addons: [],
        cartItemKey: cartItemKey, // ✅ Add the cart item key
        flatPrice: parseFloat(product.price || product.flatPrice || 0)
      };

      console.log('📦 ProductDetailsModal adding:', {
        name: productToAdd.name,
        quantity: productToAdd.quantity
      });

      onAddToCart(productToAdd);
      onClose();
    }
  };

  const totalPrice = (parseFloat(product.price || product.flatPrice || 0) * (cartQuantity > 0 ? cartQuantity : quantity)).toFixed(2);

  return (
    <div className="product-details-overlay" onClick={onClose}>
      <div className="product-details-modal" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="product-details-close" onClick={onClose}>
          <X size={24} />
        </button>

        {/* Product Image Section */}
        <div className="product-details-image-section">
          <FallbackImg
            src={product.coverImage}
            alt={product.name}
            className="product-details-image"
          />

          {/* Badges on Image */}
          <div className="product-details-badges">
            {/* Veg/Non-veg Badge */}
            {product.isVeg !== undefined && (
              <div className={`product-details-veg-badge ${product.isVeg ? 'veg' : 'non-veg'}`}>
                <div className={product.isVeg ? 'veg-dot' : 'non-veg-dot'}></div>
              </div>
            )}
          </div>

          {/* Stock Status Badges */}
          <div className="product-details-stock-badges">
            {isOutOfStock && (
              <div className="product-details-stock-tag out-of-stock">
                OUT OF STOCK
              </div>
            )}

            {isLimitedStock && !isOutOfStock && (
              <div className="product-details-stock-tag limited-stock">
                Only {Math.floor(availableQty)} left
              </div>
            )}

            {needsCustomization && !isOutOfStock && (
              <div className="product-details-stock-tag customizable">
                Customizable
              </div>
            )}
          </div>
        </div>

        {/* Product Details Section */}
        <div className="product-details-content">
          {/* Product Title */}
          <h2 className="product-details-title">{product.name}</h2>

          {product.spiceLevel > 0 && (
            <div style={{ margin: '4px 0 8px' }}>
              <SpiceLevel level={product.spiceLevel} size={16} />
            </div>
          )}

          {/* Rating */}
          {product.rating && (
            <div className="product-details-rating">
              <span className="rating-star">⭐</span>
              <span className="rating-value">{product.rating}</span>
              {product.reviews && (
                <span className="rating-reviews">({product.reviews} reviews)</span>
              )}
            </div>
          )}

          {/* Price */}
          <div className="product-details-price-section">
            <span className="product-details-price">
              {needsCustomization && !product.isCombo && 'From '}
              {settingsService.formatPrice(product.price || product.flatPrice || 0)}
              {product.priceUnit && (
                <span className="product-details-price-unit">/{product.priceUnit}</span>
              )}
            </span>
            {product.originalPrice && parseFloat(product.originalPrice) > parseFloat(product.price) && (
              <span className="product-details-original-price">{settingsService.formatPrice(product.originalPrice)}</span>
            )}
          </div>

          {/* Description */}
          {product.description && (
            <div className="product-details-description">
              <p>{product.description}</p>
            </div>
          )}

          {/* Caution */}
          {product.caution && product.caution.trim() !== '' && (
            <div className="product-details-caution">
              <AlertCircle size={16} />
              <p>{product.caution}</p>
            </div>
          )}

          {/* Stock Warning */}
          {isLimitedStock && !isOutOfStock && (
            <div className="product-details-warning">
              <AlertCircle size={18} />
              <span>Hurry! Only {Math.floor(availableQty)} units left in stock</span>
            </div>
          )}

          {/* Quantity selector, Add to Cart, and footer note removed — 
             users should add items via the product card buttons instead */}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;